<template>
  <section class="rates-update">
    <!-- CALENDÁRIO DE SELEÇÃO -->
    <b-row align-v="end">
      <b-col class="items__left-margin">
        <FormDatepicker
          v-model="dateRange"
          :vcalendarOptions="{
            disabledDates,
            isRange: true
          }"
          :maxDate="maxDate"
          label="Adicione data"
          placeholder="dd/mm/aaaa - dd/mm/aaaa"
          size="large"
        />

        <Button :disabled="disableAddDate" size="small" @click="addDateRange()"
          >Adicionar</Button
        >
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col>
        <Paragraph
          :class="{ 'mt-2': dateRange }"
          class="primary__color"
          font-size="small"
        >
          Você pode cadastrar uma tarifa com um período máximo de
          {{ maxCalendarMonths }} meses no futuro.
        </Paragraph>
      </b-col>
    </b-row>

    <!-- LISTA DE DATAS SELECIONADAS -->
    <b-row class="mt-3 selected-dates">
      <b-col class="selected-dates--chips-list">
        <Chip
          v-for="(date, i) in selectedDates"
          :key="`${date.start}-${date.end}-${i}`"
          close
          @close="removeSelectedDate(date)"
        >
          {{ formatDate(date.rawStart, 'dd MMM yyyy') }} -
          {{ formatDate(date.rawEnd, 'dd MMM yyyy') }}
        </Chip>
      </b-col>
    </b-row>

    <!-- LINHA DIVISÓRIA -->
    <b-row v-if="selectedDates.length > 0">
      <b-col>
        <SeparateLine :left="-10" :right="-10" />
      </b-col>
    </b-row>

    <!-- SELEÇÃO DE QUARTOS, TIPO DE ATUALIZAÇÃO E TARIFA (QUANDO O HOTEL TIVER MAIS DE UMA)-->
    <b-row
      v-if="selectedDates.length > 0"
      :class="{
        disabled: disableEdition
      }"
    >
      <!-- QUARTOS -->
      <b-col class="items__left-margin">
        <FormSelect
          v-model="selectedRooms"
          :options="roomsOptions"
          label="Quartos"
          size="large"
          multiple
        />

        <!-- TIPO ATUALIZAÇÃO -->
        <FormSelect
          v-model="selectedUpdateType"
          :options="updateTypeOptions"
          label="Tipo de atualização"
          size="large"
        />

        <!-- TARIFA (QUANDO O HOTEL TIVER MAIS DE UMA) E FOR ABSOLUTA -->
        <FormSelect
          v-if="ratesOptions.length > 1"
          v-model="selectedRate"
          :options="ratesOptions"
          label="Tarifa"
          size="large"
        />
      </b-col>
    </b-row>

    <!-- EDIÇÃO DOS VALORES DOS QUARTOS -->
    <b-row
      v-if="selectedRooms.length > 0"
      :class="{
        disabled: disableEdition
      }"
      class="rooms-rates-edition"
    >
      <b-col>
        <Paragraph
          class="rooms-rates-edition--title"
          font-size="large"
          font-weight="light"
          >Edição dos valores dos quartos
        </Paragraph>
        <transition-group name="fade" appear>
          <RoomCard
            v-for="room in selectedRooms"
            v-model="editedRules"
            :cachedValue="
              editedRulesCache.find((item) => item.code === room.value)
            "
            :key="`room-${room.value}`"
            :roomData="rooms.find((item) => item.codigo === room.value)"
            :typeUpdate="selectedUpdateType.value"
            @remove="removeRoom"
          />
        </transition-group>
      </b-col>
    </b-row>

    <AlertModal
      ref="alertModal"
      v-model="showAlertModal"
      text="Excluindo todos os períodos adicionados, não será possível adicionar valores ao calendário. Deseja continuar?"
    />
  </section>
</template>

<script>
/** Componente da Tab de atualização de tarifas do hotel */
import { format } from 'date-fns' // https://github.com/date-fns/date-fns
import { mapGetters } from 'vuex'
import pt from 'date-fns/locale/pt-BR'
import FormDatepicker from '@solucoes/components-lib-tmp/src/components/elements/FormDatepicker'
import FormSelect from '@/components/_base/elements/FormSelect'
import Button from '@/components/_base/elements/Button'
import Paragraph from '@/components/_base/elements/Paragraph'
import Chip from '@/components/_base/patterns/Chip'
import AlertModal from '@/components/_base/patterns/AlertModal'
import SeparateLine from '@/components/_base/patterns/SeparateLine'

import RoomCard from './RoomCard'

export default {
  name: 'RatesUpdate',
  components: {
    FormDatepicker,
    FormSelect,
    Button,
    Paragraph,
    Chip,
    RoomCard,
    AlertModal,
    SeparateLine
  },
  props: {
    /** Lista de quartos do hotel */
    rooms: {
      type: Array,
      default: () => []
    },
    /** Objeto com datas e quartos editados */
    value: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      maxDate: null /** Parâmetro para controle de data máxima */,
      dateRange: null /** Parâmetro para controle de seleção de data no calendário */,
      selectedDates: [] /** Períodos selecionados */,
      selectedRooms: [] /** Quartos selecionados */,
      selectedUpdateType: {
        text: 'Valor',
        value: 1
      } /** Inicia Valor como default */,
      selectedRate: [] /** quando o hotel tiver mais de uma tarifa */,
      updateTypeOptions: [
        { text: 'Valor', value: 1 },
        { text: 'Desconto', value: 2 }
      ] /** Opções do tipo de atualização */,
      ratesOptions: [] /** Opções de tarifas */,
      editedRules: [] /** Regras selecionadas */,
      editedRulesCache: [] /** Cache de regras selecionada */,
      showAlertModal: false /** Flag para apresentar a modal de alerta */,
      disableEdition: false /** Flag que habilita/ desabilita a edição */,
      maxCalendarMonths: null
    }
  },
  computed: {
    /** Desabilita o botão de adicionar data */
    disableAddDate() {
      return !this.dateRange
    },
    /** Padroniza o array para popular o select */
    roomsOptions() {
      return this.rooms.map((item) => {
        return {
          text: item.nome,
          value: item.codigo
        }
      })
    },
    /** Retorna as datas que devem ser desabilitadas da seleção */
    disabledDates() {
      return this.selectedDates.map((item) => {
        const obj = {
          start: item.rawStart,
          end: item.rawEnd
        }

        return obj
      })
    },
    ...mapGetters('hotel', ['getResetForm'])
  },
  watch: {
    /**
     * Verifica alterações em produndidade no array de quartos
     * Emite um evento de mudança
     * ? pensar em uma forma de melhorar essa comunicação
     * */
    editedRules: {
      handler(newValue) {
        /** Monta um objeto com os dados pertinentes para montar o calendário */
        const obj = {
          dates: this.selectedDates,
          editedRates: newValue,
          rate: this.ratesOptions,
          selectedRate: this.selectedRate
        }
        this.$emit('input', obj)
      },
      deep: true
    },
    selectedRooms: {
      handler(newValue, oldValue) {
        if (newValue.length === 0) {
          this.editedRules = []
          return
        }

        /** Remove o quarto das regras editadas */
        if (newValue.length < oldValue.length) {
          const diff = oldValue.filter(
            (newItem) =>
              !newValue.find((oldItem) => oldItem.value === newItem.value)
          )

          /** Se encontrou diferença */
          if (diff.length > 0) {
            /** Pega a posição no array */
            const idx = this.editedRules.findIndex(
              (item) => item.code === diff[0].value
            )

            /** Remove */
            if (idx > -1) {
              /** Salva a regra removida no cache */
              this.addRemovedRoomRulesInCache(this.editedRules[idx])

              /** Excluí a regra */
              this.editedRules.splice(idx, 1)
            }
          }
        }
      }
    },
    selectedRate: {
      handler(newValue) {
        /** Monta um objeto com os dados pertinentes para montar o calendário */
        const obj = {
          selectedRate: newValue,
          dates: this.selectedDates,
          editedRates: this.editedRules
        }
        this.$emit('input', obj)
      }
    },

    getResetForm(newValue) {
      if (newValue) {
        this.selectedRooms = []
        this.selectedDates = []
      }
    }
  },
  created() {
    /** Realiza chamada da quantidade de meses */
    this.$calendar.getMaxMonths().then((response) => {
      this.maxCalendarMonths = response.data.numeroMesesCalendario
      const dateInicio = new Date()

      dateInicio.setMonth(dateInicio.getMonth() + this.maxCalendarMonths)

      this.maxDate = dateInicio
    })

    /** Emite um evento com os valores inciais do componente para o pai */
    this.$emit('input', {
      dates: this.selectedDates,
      editedRates: this.editedRules,
      rate: this.ratesOptions,
      selectedRate: this.selectedRate
    })

    this.$rate.getAbsoluteRates(this.$route.params.id).then((response) => {
      const data = response.data

      if (data.length > 0) {
        this.ratesOptions = data.map((item) => {
          const obj = {
            text: item.nome,
            value: item.codigo
          }

          return obj
        })

        this.selectedRate = this.ratesOptions[0]
      }
    })
  },
  methods: {
    /**
     * Adiciona ou substituí as regras de um quarto que foi removido
     * @param {Object} removedRule objeto com as regras do quarto removido
     */
    addRemovedRoomRulesInCache(removedRule) {
      /** Procura se já existe uma entrada para as regras do quarto */
      const idx = this.editedRulesCache.findIndex(
        (cachedRule) => cachedRule.code === removedRule.code
      )

      /** Se existe, substituí */
      if (idx > -1) {
        this.editedRulesCache[idx] = removedRule
      } else {
        this.editedRulesCache.push(removedRule)
      }
    },
    /** Adiciona a data selecionada ao array de seleções */
    addDateRange() {
      this.selectedDates.push({
        rawStart: this.dateRange.start,
        start: format(this.dateRange.start, 'yyyy-MM-dd'),
        rawEnd: this.dateRange.end,
        end: format(this.dateRange.end, 'yyyy-MM-dd')
      })
      this.dateRange = null
      this.disableEdition = false
    },
    /**
     * Formata uma data de acordo com a string passada
     * @param {Date} date Date obj da data a ser formatada
     * @param {String} formatStr String com o formato final da data
     * @returns {String}
     */
    formatDate(date, formatStr = 'dd/MM/yyyy') {
      return format(date, formatStr, { locale: pt })
    },
    /**
     * Verificas se um dos valores foi preenchido
     *
     * @return {Boolean}
     */
    hasFilledRules() {
      return this.editedRules.some((item) =>
        item.rules.some((rule) => {
          /** propriedades para ignorar na validação */
          const ignoredEntries = ['roomCode', 'selectedWeekDays']
          let returnValue = false

          /**
           * Verifica se em algumas das regras selecionadas
           * existe ao menos 1 quarto com um valor adicionado
           * */
          for (const entry of Object.entries(rule)) {
            if (ignoredEntries.includes(entry[0])) continue

            /** Se sim retorna true */
            if (entry[1] > 0) {
              returnValue = true
            }
          }

          return returnValue
        })
      )
    },
    /**
     * Remove uma data do array de seleção
     * @param {Object} date Objeto com o período selecionado
     */
    removeSelectedDate(date) {
      if (this.selectedDates.length === 1 && this.hasFilledRules()) {
        /** Apresenta um alerta e aguarda a confirmação */
        this.$refs.alertModal
          .openModal()
          .then((response) => {
            if (response) {
              this.removeDateFromArray(date)
              this.disableEdition = true
            }
          })
          .catch()
      } else {
        this.removeDateFromArray(date)

        /** Se não existe data selecionada limpa as seleções de quartos */
        if (this.selectedDates.length === 0) {
          this.selectedRooms = []
        }
      }
    },
    /** Remove uma entrada do array de datas */
    removeDateFromArray(date) {
      const foundDate = this.selectedDates.findIndex(
        (item) => item.rawStart === date.rawStart && item.rawEnd === date.rawEnd
      )

      if (foundDate > -1) {
        this.selectedDates.splice(foundDate, 1)
      }
    },
    /**
     * Remove uma acomodação selecionada
     * @param {Object} room dados da acomodação removida
     */
    removeRoom(room) {
      const removed = this.selectedRooms.findIndex(
        (item) => item.value === room.codigo
      )

      if (removed > -1) {
        /**
         * Cria uma cópia dos valores, altera e substituí o valor atual
         * para previnir o comportamento da vm-watch, apresentada na nota do link
         * https://vuejs.org/v2/api/#vm-watch
         */
        const updatedValues = [...this.selectedRooms]
        updatedValues.splice(removed, 1)
        this.selectedRooms = updatedValues
      }

      /** Procura no array de regras o quarto para remover */
      const roomIndex = this.editedRules.findIndex(
        (item) => +item.code === +room.codigo
      )

      if (roomIndex > -1) {
        /** Salva no cache as regras removidas */
        this.addRemovedRoomRulesInCache(this.editedRules[roomIndex])

        /** Remove a regra */
        this.editedRules.splice(roomIndex, 1)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.rooms-rates-edition {
  position: relative;
  padding-top: 90px;

  .disabled {
    pointer-events: none;
    cursor: default;
    opacity: 0.1;
  }

  ::v-deep .room-card {
    & + .room-card {
      margin-top: 20px;
    }
  }

  &:before {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: -40px;
    width: calc(100% + 80px);
    height: 35px;
    @include box-shadow(0px 20px 30px -14px rgb(0 0 0 / 40%));
  }

  &--title {
    margin-bottom: 17px;
  }
}

/** TODO corrigir quando houver quebra de linha  */
.selected-dates {
  .chip {
    border: none;
    box-shadow: 0 0 0 1px $gray-border;
    margin-top: 9px;
  }
}

.items {
  &__left-margin {
    & > * {
      margin-left: 20px;

      &:first-child {
        margin-left: 0;
      }
    }
  }
}
</style>
